import { ChangeEvent, FocusEvent, ReactNode } from "react";
import {
  Box,
  Divider,
  FormHelperText,
  InputAdornment,
  Slider,
  TextField,
} from "@mui/material";
import { Typography } from "@mui/material";
import { FormikErrors, FormikTouched } from "formik";
import { ControlledSelect, makeSxStyles } from "ui";
import {
  DrivewayCondition,
  DrivewaySlope,
  drivewayConditionSelectOptions,
  drivewaySlopeSelectOptions,
  DRIVEWAY_DIFFICULTY_MAX,
  DRIVEWAY_DIFFICULTY_MIN,
} from "service/types/enum/driveway-enum";
import FieldRow from "@/shared/components/FieldRow";
import { useNumericFields } from "@/shared/hooks/useNumericFields";
import { FormikSetters } from "@/shared/types/formik";
import { numericFieldSx } from "@/shared/utils/numeric-input";
import BillingAddressAutocomplete from "./BillingAddressAutocomplete";

// The CRM fields shared by the lead form and the customer form.
export interface CrmFieldValues {
  onsiteContactName?: string;
  onsiteContactEmail?: string;
  contractPrice?: number;
  billingAddress?: string;
  financialNotes?: string;
  squareFootage?: number;
  slope?: DrivewaySlope;
  condition?: DrivewayCondition;
  difficulty?: number;
  notes?: string;
}

export type CrmSection = "onsiteContact" | "finances" | "drivewayInfo";

const DEFAULT_SECTION_ORDER: CrmSection[] = [
  "onsiteContact",
  "finances",
  "drivewayInfo",
];

interface CrmDetailsFieldsProps extends FormikSetters {
  values: CrmFieldValues;
  errors: FormikErrors<CrmFieldValues>;
  touched: FormikTouched<CrmFieldValues>;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
  // Which sections to render, in order. Customer form uses a subset
  // (Onsite Contact + Driveway Info); the lead form uses all three.
  sectionOrder?: CrmSection[];
}

const clampDifficulty = (value: number) =>
  Math.min(DRIVEWAY_DIFFICULTY_MAX, Math.max(DRIVEWAY_DIFFICULTY_MIN, value));

const CrmDetailsFields = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  sectionOrder = DEFAULT_SECTION_ORDER,
}: CrmDetailsFieldsProps) => {
  const styles = useStyles();

  // The numeric text inputs store a number (or undefined when cleared) so they
  // satisfy the generated `z.number().optional()` schema; unparseable input
  // stores NaN so the schema reports it instead of the field silently clearing.
  const numeric = useNumericFields({
    setFieldValue,
    setFieldTouched,
    handleBlur,
  });

  const price = numeric.field({
    name: "contractPrice",
    value: values.contractPrice,
    error: errors.contractPrice,
    touched: touched.contractPrice,
    allowDecimal: true,
  });
  const footage = numeric.field({
    name: "squareFootage",
    value: values.squareFootage,
    error: errors.squareFootage,
    touched: touched.squareFootage,
    allowDecimal: true,
  });
  // Stepping is wanted here: min/max bound it to 1–5 and the value is a whole
  // rating, so Up/Down (and the spin buttons) are useful.
  const difficulty = numeric.field({
    name: "difficulty",
    value: values.difficulty,
    error: errors.difficulty,
    touched: touched.difficulty,
    allowStepping: true,
    clamp: clampDifficulty,
  });

  const renderOnsiteContact = (): ReactNode => (
    <>
      <Typography sx={styles.sectionTitle}>Onsite Contact</Typography>
      <FieldRow label="Onsite Contact Name">
        <TextField
          id="onsiteContactName"
          value={values.onsiteContactName ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.onsiteContactName && !!errors.onsiteContactName}
          helperText={touched.onsiteContactName && errors.onsiteContactName}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Onsite Contact Email">
        <TextField
          id="onsiteContactEmail"
          value={values.onsiteContactEmail ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.onsiteContactEmail && !!errors.onsiteContactEmail}
          helperText={touched.onsiteContactEmail && errors.onsiteContactEmail}
          fullWidth
        />
      </FieldRow>
    </>
  );

  const renderFinances = (): ReactNode => (
    <>
      <Typography sx={styles.sectionTitle}>Finances</Typography>
      <FieldRow label="Contract Price">
        <TextField
          id="contractPrice"
          type="number"
          value={price.value}
          onChange={price.onChange}
          onFocus={price.onFocus}
          onBlur={price.onBlur}
          error={price.showError}
          helperText={price.helperText}
          inputProps={price.inputProps}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={numericFieldSx}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Billing Address">
        <BillingAddressAutocomplete
          value={values.billingAddress ?? ""}
          onChange={(v) => setFieldValue("billingAddress", v)}
          onBlur={handleBlur}
          error={touched.billingAddress && !!errors.billingAddress}
          helperText={touched.billingAddress && errors.billingAddress}
        />
      </FieldRow>
      <FieldRow label="Financial Notes">
        <TextField
          id="financialNotes"
          value={values.financialNotes ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.financialNotes && !!errors.financialNotes}
          helperText={touched.financialNotes && errors.financialNotes}
          fullWidth
          multiline
          minRows={3}
        />
      </FieldRow>
    </>
  );

  const renderDrivewayInfo = (): ReactNode => (
    <>
      <Typography sx={styles.sectionTitle}>Driveway Info</Typography>
      <FieldRow label="Square Footage">
        <TextField
          id="squareFootage"
          type="number"
          value={footage.value}
          onChange={footage.onChange}
          onFocus={footage.onFocus}
          onBlur={footage.onBlur}
          error={footage.showError}
          helperText={footage.helperText}
          inputProps={footage.inputProps}
          InputProps={{
            endAdornment: <InputAdornment position="end">sq ft</InputAdornment>,
          }}
          sx={numericFieldSx}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Slope">
        <ControlledSelect
          fullWidth
          placeholder="Slope"
          options={drivewaySlopeSelectOptions}
          value={values.slope}
          onChange={(value) => {
            setFieldValue("slope", value);
            setFieldTouched("slope", true);
          }}
        />
      </FieldRow>
      <FieldRow label="Condition">
        <ControlledSelect
          fullWidth
          placeholder="Condition"
          options={drivewayConditionSelectOptions}
          value={values.condition}
          onChange={(value) => {
            setFieldValue("condition", value);
            setFieldTouched("condition", true);
          }}
        />
      </FieldRow>
      <FieldRow
        label="Difficulty"
        description="Rate driveway access - 1 is easiest, 5 is most difficult."
      >
        <Box sx={styles.difficulty}>
          <TextField
            id="difficulty"
            type="number"
            value={difficulty.value}
            onChange={difficulty.onChange}
            onFocus={difficulty.onFocus}
            onBlur={difficulty.onBlur}
            inputProps={{
              ...difficulty.inputProps,
              min: DRIVEWAY_DIFFICULTY_MIN,
              max: DRIVEWAY_DIFFICULTY_MAX,
              style: { textAlign: "center" },
            }}
            sx={styles.difficultyInput}
          />
          <Slider
            value={
              difficulty.value === ""
                ? DRIVEWAY_DIFFICULTY_MIN
                : difficulty.value
            }
            onChange={(_, val) => difficulty.setValue(val as number)}
            min={DRIVEWAY_DIFFICULTY_MIN}
            max={DRIVEWAY_DIFFICULTY_MAX}
            step={1}
            sx={styles.slider}
          />
        </Box>
        {difficulty.helperText && (
          <FormHelperText error sx={styles.difficultyError}>
            {difficulty.helperText}
          </FormHelperText>
        )}
      </FieldRow>
      <FieldRow
        label="Notes"
        description="Visible to the assigned driver during the plow session."
      >
        <TextField
          id="notes"
          value={values.notes ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.notes && !!errors.notes}
          helperText={touched.notes && errors.notes}
          fullWidth
          multiline
          minRows={3}
        />
      </FieldRow>
    </>
  );

  const sectionRenderers: Record<CrmSection, () => ReactNode> = {
    onsiteContact: renderOnsiteContact,
    finances: renderFinances,
    drivewayInfo: renderDrivewayInfo,
  };

  return (
    <Box>
      {sectionOrder.map((key) => (
        <Box key={key}>
          <Divider sx={styles.divider} />
          {sectionRenderers[key]()}
        </Box>
      ))}
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  divider: {
    my: "24px",
  },
  sectionTitle: {
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: "#c5c6c9",
    mb: "20px",
  },
  difficulty: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "16px",
  },
  difficultyInput: {
    width: "64px",
    flexShrink: 0,
  },
  // Matches the inset MUI gives a TextField's own helperText.
  difficultyError: {
    mx: "14px",
  },
  slider: {
    flex: 1,
  },
}));

export default CrmDetailsFields;
